import {useEffect, useState} from "react";
import {useForm} from "antd/es/form/Form";
import {StatisticItem} from "../types/statistic";
import {statisticService} from "../services/statistic-service";
import {openSuccessNotification, showErrorNotification} from "../helpers/mainHelpers";
import {Modal} from "antd";
import {DeleteOutlined} from "@ant-design/icons";


export const useStatisticController = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(0)
  const [selectedStatisticItem, setSelectedStatisticItem] = useState<StatisticItem | undefined>(undefined)
  const [statisticItemsList, setStatisticItemsList] = useState<StatisticItem[]>([])
  const [form] = useForm()

  useEffect(() => {
    fetchStatisticList([])
  }, [lastUpdate])

  useEffect(() => {
    if (!isModalOpen) {
      form.resetFields()
      setSelectedStatisticItem(undefined)
    }
  }, [isModalOpen])

  const deleteStatisticItem = async (id: string) => {
    setIsLoading(true)
    try {
      await statisticService.delete(id)
      const updatedList = statisticItemsList.filter(item => item.$id !== id)
      setStatisticItemsList(updatedList)
      openSuccessNotification("Запис успішно видалено!")
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка видалення запису!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const confirmDelete = (props: { id: string }) => {
    const {confirm} = Modal;
    confirm(
      {
        width: 400,
        icon: <DeleteOutlined/>,
        title: `Видалити запис?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          deleteStatisticItem(props.id)
        },
      });
  }

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen)
  }

  const editButtonHandler = (statisticItem: StatisticItem) => {
    setSelectedStatisticItem(statisticItem)
    toggleModal()
  }

  const toggleLastUpdate = () => {
    setLastUpdate(Date.now())
  }

  const createStatisticItem = async (data: StatisticItem) => {
    setIsLoading(true)
    try {
      await statisticService.create(data)
      openSuccessNotification("Запис статистики успішно створено!")
      toggleModal()
      toggleLastUpdate()
    } catch (error) {
      showErrorNotification({message: "Помилка при створенні запису статистики!", description: JSON.stringify(error)})
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }
  const updateStatisticItem = async (data: StatisticItem) => {
    setIsLoading(true)
    try {
      await statisticService.update({$id: selectedStatisticItem?.$id, ...data})
      toggleModal()
      toggleLastUpdate()
      openSuccessNotification("Дані статистики успішно оновлено!")
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка при оновленні запису статистики!", description: JSON.stringify(error)})
    } finally {
      setIsLoading(false)
    }
  }

  const fetchStatisticList = async (queries: string[]) => {
    setIsLoading(true)
    try {
      const list = await statisticService.list(queries)
      setStatisticItemsList(list)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка при отриманні записів статистики!"})
    } finally {
      setIsLoading(false)
    }
  }

  const submitButtonHandler = async (data: StatisticItem) => {
    if (selectedStatisticItem) {
      await updateStatisticItem(data)
    } else {
      await createStatisticItem(data)
    }
  }

  return {
    isLoading,
    form,
    isModalOpen,
    toggleModal,
    selectedStatisticItem,
    statisticItemsList,
    editButtonHandler,
    submitButtonHandler,
    confirmDelete
  }
}